import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';

export interface SectionText {
  title: {
    cs: string;
    en: string;
    de: string;
    sk: string;
  };
  description: {
    cs: string;
    en: string;
    de: string;
    sk: string;
  };
}

export interface RoboticsSections {
  products: SectionText;
  services: SectionText;
  references: SectionText;
}

const defaultSections: RoboticsSections = {
  products: {
    title: {
      cs: 'Produkty',
      en: 'Products',
      de: 'Produkte',
      sk: 'Produkty'
    },
    description: {
      cs: 'Objevte naše nejmodernější robotické systémy a komponenty',
      en: 'Discover our cutting-edge robotic systems and components',
      de: 'Entdecken Sie unsere modernsten Robotiksysteme und Komponenten',
      sk: 'Objavte naše najmodernejšie robotické systémy a komponenty'
    }
  },
  services: {
    title: {
      cs: 'Nesériové výrobky',
      en: 'Custom Products',
      de: 'Sonderanfertigungen',
      sk: 'Nesériové výrobky'
    },
    description: {
      cs: 'Zákaznický vývoj – zakázková výroba a speciální projekty na míru',
      en: 'Custom development – bespoke manufacturing and special projects',
      de: 'Kundenentwicklung – Sonderfertigung und Spezialprodukte nach Maß',
      sk: 'Zákaznícky vývoj – zákazková výroba a špeciálne projekty na mieru'
    }
  },
  references: {
    title: {
      cs: 'Reference',
      en: 'References',
      de: 'Referenzen',
      sk: 'Referencie'
    },
    description: {
      cs: 'Úspěšně realizované projekty a spokojení zákazníci',
      en: 'Successfully completed projects and satisfied customers',
      de: 'Erfolgreich abgeschlossene Projekte und zufriedene Kunden',
      sk: 'Úspešne zrealizované projekty a spokojní zákazníci'
    }
  }
};

export function useRoboticsSections() {
  const [sections, setSections] = useState<RoboticsSections>(defaultSections);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadSections = async () => {
    try {
      setLoading(true);
      setError(null);

      const { data, error: fetchError } = await supabase
        .from('site_settings')
        .select('value')
        .eq('key', 'robotics_sections')
        .maybeSingle();

      if (fetchError) throw fetchError;

      if (data?.value) {
        const parsed = JSON.parse(data.value);
        setSections(parsed);
      } else {
        setSections(defaultSections);
      }
    } catch (err) {
      console.error('Error loading robotics sections:', err);
      setError(err instanceof Error ? err.message : 'Failed to load sections');
      setSections(defaultSections);
    } finally {
      setLoading(false);
    }
  };

  const updateSections = async (newSections: RoboticsSections) => {
    try {
      setError(null);

      const { data: existing } = await supabase
        .from('site_settings')
        .select('id')
        .eq('key', 'robotics_sections')
        .maybeSingle();

      const value = JSON.stringify(newSections);

      if (existing) {
        const { error: updateError } = await supabase
          .from('site_settings')
          .update({ value })
          .eq('key', 'robotics_sections');

        if (updateError) throw updateError;
      } else {
        const { error: insertError } = await supabase
          .from('site_settings')
          .insert({
            key: 'robotics_sections',
            value,
            description: 'Section texts for Cybertronic Robotics page'
          });

        if (insertError) throw insertError;
      }

      setSections(newSections);
    } catch (err) {
      console.error('Error updating robotics sections:', err);
      setError(err instanceof Error ? err.message : 'Failed to update sections');
      throw err;
    }
  };

  useEffect(() => {
    loadSections();
  }, []);

  return {
    sections,
    loading,
    error,
    updateSections,
    reload: loadSections
  };
}
